import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { saveAs } from "file-saver";
import { ZplTemplate } from "../../config/newZebraTemplate";
import { downloadPdf } from '../../api/downloadPdf'
import useAuth from "../../hooks/useAuth";
import { showToast } from '../../utils/showToast'
import { apiBodyMessage } from '../../utils/apiErrorMessage'
import { buildExchangeZplLine, multipleDownloadZPLFile } from '../../utils/downloadZebra'
import { buildAdditionalInfoForZpl } from "../../utils/labelAdditionalInfo";
import { zplField } from "../../utils/zplSafe";
import fileName from "../../utils/fileName";
import QRCode from "react-qr-code";
import { fetchLogos } from "../../utils/fetchLogos";
import { createReturnLabel } from "../../api/packages";
import { CopyButton } from "../ui/CopyButton";
import { isCallablePhone } from "../evidenceView/resolution";
import { ArrowRightLeft } from "lucide-react";
import { canCreateExchange } from "../../utils/orderActions";
import CreateExchangeDialog from "./CreateExchangeDialog";

function getFormattedDate() {
  const months = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  const date = new Date();
  const day = String(date.getDate()).padStart(2, '0');
  const month = months[date.getMonth()];
  return `${day} - ${month}`;
}


// `onExchangeChanged` (opcional): avisa al contenedor que el pedido tiene un cambio
// nuevo o en curso, para que lo recargue (el detalle muestra el cambio). Se llama
// también con el 409: si la respuesta del POST se perdió y el reintento choca con
// el hijo ya creado, el contenedor igual tiene que enterarse.
export default function DownloadVoucher({ defaultData, existingReturnLabel, onExchangeChanged }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [logos, setLogos] = useState({ logo: null, zplLogo: null });
  const [isLoadingLogos, setIsLoadingLogos] = useState(true);
  const [isCreatingReturn, setIsCreatingReturn] = useState(false);
  const [returnCreated, setReturnCreated] = useState(!!existingReturnLabel);
  const [returnData, setReturnData] = useState(existingReturnLabel || null);
  const [copied, setCopied] = useState(false);
  const [exchangeOpen, setExchangeOpen] = useState(false);
  // Hijo de cambio creado (o el que ya estaba en curso, si el backend respondió
  // 409): { followingNumber, inProgress } | null.
  const [exchangeChild, setExchangeChild] = useState(null);
  // El backend rechazó la etiqueta de devolución porque el padre ya se devolvió
  // (o se está devolviendo) con un cambio: no se vuelve a ofrecer en esta apertura.
  const [returnBlockedByExchange, setReturnBlockedByExchange] = useState(false);

  useEffect(() => {
    const loadLogos = async () => {
      setIsLoadingLogos(true);
      const fetchedLogos = await fetchLogos(user);
      if (fetchedLogos) {
        setLogos(fetchedLogos);
      }
      setIsLoadingLogos(false);
    };

    loadLogos();
  }, [user]);

  const handleCreateReturnLabel = async () => {
    if (!defaultData?._id) return;
    setIsCreatingReturn(true);
    const { success, data, error } = await createReturnLabel(defaultData._id);
    setIsCreatingReturn(false);
    if (success) {
      setReturnCreated(true);
      setReturnData(data);
      const num = data?.followingNumber;
      showToast(
        num
          ? t("Devolución #{{num}} creada correctamente.", { num })
          : t("Etiqueta de devolución creada correctamente."),
        true
      );
    } else if (error?.code === "returned_by_exchange" || error?.code === "exchange_in_progress") {
      // Etiqueta y cambio se excluyen sobre un mismo padre: el producto ya se retiró
      // (o se va a retirar) con el pedido de cambio.
      const number = error.followingNumber;
      const inProgress = error.code === "exchange_in_progress";
      showToast(
        inProgress
          ? number
            ? t("Este pedido tiene un cambio de producto en curso (#{{number}}).", { number })
            : t("Este pedido tiene un cambio de producto en curso.")
          : number
            ? t("Este pedido ya se devolvió con un cambio de producto (#{{number}}).", { number })
            : t("Este pedido ya se devolvió con un cambio de producto."),
        "warning"
      );
      setReturnBlockedByExchange(true);
      if (inProgress) setExchangeChild({ followingNumber: number ?? null, inProgress: true });
    } else {
      const message = error?.message || t("Error al crear la etiqueta de devolución.");
      showToast(message, false);
    }
  };

  const handleCopyReturnNumber = () => {
    const num = returnData?.followingNumber;
    if (!num) return;
    navigator.clipboard.writeText(String(num)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };


  const buildZpl = async () => {
    if (!logos.logo.zplLogo) {
      showToast(t("No se pudo cargar el logo ZPL."), false);
      return;
    }

    let zplFormatAllPackages = "";

    const totalCopiesRaw = Number(defaultData?.additionalLabels);
    const totalCopies = Number.isFinite(totalCopiesRaw) && totalCopiesRaw > 0
      ? Math.floor(totalCopiesRaw)
      : 1;

    for (let copyIndex = 1; copyIndex <= totalCopies; copyIndex++) {
      let zplFormat = ZplTemplate;
      const businessName = defaultData?.senderInfo?.businessName;
      const senderName = defaultData?.senderUser?.senderName ?? "";
      const senderLastName = defaultData?.senderUser?.senderLastName ?? ""; 
      const comunaVendedor = defaultData?.senderInfo?.senderMunicipality + ' ' + defaultData?.senderInfo?.senderState
      const nameToUse = businessName || (senderName + " " + senderLastName);
      const receiverName = defaultData?.receiver_name ?? "";
      const receiverLastName = defaultData?.receiver_lastName ?? "";
      const receiverNameToUse = (receiverName + " " + receiverLastName)
      const fechaActual = getFormattedDate();
      const logoClienteZpl = logos.logo.zplLogo || "";
      const transportistaName = defaultData?.deliveryInfo?.deliveryBusinessName;
      const state = defaultData?.shipping_address?.city?.name;

      zplFormat = zplField(
        zplFormat,
        "{{transportistaName}}",
        transportistaName
          .toString()
          .replace(/[a-zA-ZñÑáéíóúÁÉÍÓÚ]+/g, (match) => match.toUpperCase()),
      );

      zplFormat = zplField(zplFormat, "{{logoCliente}}", logoClienteZpl);

      zplFormat = zplField(
        zplFormat,
        "{{address}}",
        `${defaultData?.senderInfo?.senderAddress?.slice(0, 60) ?? ""}`,
      );
      zplFormat = zplField(zplFormat, "{{address2}}", comunaVendedor);
      zplFormat = zplField(
        zplFormat,
        "{{city}}",
        `${defaultData?.shipping_address?.municipality?.name ?? "Comuna no asignada"}`,
      );
      zplFormat = zplField(zplFormat, "{{state}}", `${state}`);
      zplFormat = zplField(zplFormat, "{{emitter.name}}", nameToUse);
      zplFormat = zplField(
        zplFormat,
        "{{emitter.phone}}",
        `${!!defaultData?.senderUser?.phone ? defaultData?.senderUser?.phone : ""}`,
      );
      zplFormat = zplField(zplFormat, "{{receiver.name}}", receiverNameToUse);
      zplFormat = zplField(
        zplFormat,
        "{{receiver.phone}}",
        `${defaultData?.receiver_phone ?? "Teléfono no asignado"}`,
      );
      zplFormat = zplField(
        zplFormat,
        "{{receiver.address_line}}",
        `${defaultData?.shipping_address?.address_line?.slice(0, 50) ?? ""}`,
      );
      zplFormat = zplField(
        zplFormat,
        "{{receiver.address_line2}}",
        `${defaultData?.shipping_address?.address_line?.slice(50) ?? ""} ${
          defaultData?.shipping_address?.city?.name ?? ""
        }`,
      );
      zplFormat = zplField(
        zplFormat,
        "{{receiver.comment}}",
        buildAdditionalInfoForZpl(
          defaultData?.shipping_address?.comment,
          defaultData?.comments,
        ),
      );

      zplFormat = zplField(zplFormat, "{{packageKey}}", `${defaultData?._id}`);
      zplFormat = zplField(
        zplFormat,
        "{{followingNumber}}",
        `${defaultData?.followingNumber}`,
      );
      zplFormat = zplField(
        zplFormat,
        "{{comments}}",
        `${defaultData?.comments ?? defaultData?.shipping_address?.comment ?? "Sin comentarios"}`,
      );
      zplFormat = zplField(zplFormat, "{{order_id}}", `${defaultData?.order_id ?? ""}`);
      zplFormat = zplField(zplFormat, "{{fecha}}", fechaActual);

      const bultoLine =
        totalCopies > 1
          ? `^FO420,300^A0N,28,32^FB720,1,0,L^FH^FDBULTO: ${copyIndex} DE ${totalCopies}^FS`
          : "";

      zplFormat = zplField(zplFormat, "{{bultoLine}}", bultoLine);
      zplFormat = zplField(zplFormat, "{{exchangeLine}}", buildExchangeZplLine(defaultData));

      zplFormat = zplFormat.replaceAll("ñ", "n");
      zplFormat = zplFormat.replaceAll("Ñ", "N");

      zplFormatAllPackages += zplFormat;
    }
    return zplFormatAllPackages;
  };

  const tryDownloadPdf = async () => {
    const { data, error } = await downloadPdf({
      clientId: user?._id,
      packages: [defaultData],
    });

    if(error) showToast(apiBodyMessage(error) ?? t("Error al descargar PDF."), false)

    if(data) {
      showToast(t("Se ha generado el PDF correctamente."), true)
      window.open(data?.url)
    }

  }

    const downloadZpl = async () => {
    try {
      const zpl = await buildZpl();
      if (!zpl) return; // ya se mostró toast si faltaba el logo

      const blob = new Blob([zpl], { type: "text/plain;charset=utf-8" });
      // Usa tu helper para el nombre
      const fname = fileName("zpl"); // e.g. "etiqueta_2025-09-05.zpl"
      saveAs(blob, fname);

      showToast(t("Etiqueta ZPL generada correctamente."), true);
    } catch (err) {
      console.error(err);
      showToast(t("Error al generar la etiqueta ZPL."), false);
    }
  };

  const txt = async () => {

    //console.log("user en txt", user);
    const element = document.createElement('a')
    const zplFinal = await multipleDownloadZPLFile({packages:  [defaultData], user })

    element.setAttribute(
      'href',
      `data:text/plain;charset=utf-8,${encodeURIComponent(zplFinal)}`
    );

    element.setAttribute('download', fileName("txt"));

    element.style.display = 'none';
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
    // setConfirmationModal(false)
  }

  // Función auxiliar para limpiar valores undefined
  const cleanValue = (value) => {
    // Si es undefined o null, retornar string vacío
    if (value === undefined || value === null) {
      return '';
    }
    
    // Convertir a string
    let stringValue = String(value);
    
    // Si el string está vacío o es solo "undefined", retornar vacío
    stringValue = stringValue.trim();
    if (stringValue === '' || stringValue.toLowerCase() === 'undefined') {
      return '';
    }
    
    // Remover cualquier ocurrencia de la palabra "undefined" (case-insensitive)
    // \b asegura que sea una palabra completa, no parte de otra palabra
    let cleaned = stringValue.replace(/\bundefined\b/gi, '');
    
    // Remover espacios en blanco al inicio y final
    cleaned = cleaned.trim();
    
    // Remover espacios múltiples
    cleaned = cleaned.replace(/\s+/g, ' ');
    
    // Si después de limpiar queda vacío, retornar vacío
    if (cleaned === '' || cleaned.toLowerCase() === 'undefined') {
      return '';
    }
    
    return cleaned;
  };

  // Función para limpiar strings concatenados que pueden tener "undefined"
  const cleanConcatenated = (...values) => {
    return values
      .map(v => cleanValue(v))
      .filter(v => v && v !== 'undefined' && v !== '')
      .join(' ')
      .replace(/\bundefined\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const receiverName = cleanValue(defaultData?.receiver_name);
  const receiverLastName = cleanValue(defaultData?.receiver_lastName);
  const fullReceiverName = cleanConcatenated(receiverName, receiverLastName) || t('Sin nombre registrado');
  
  const receiverPhone = cleanValue(defaultData?.receiver_phone) || t('Sin número de teléfono registrado');
  const municipality = cleanValue(defaultData?.shipping_address?.municipality?.name) || t('Sin comuna registrada');
  
  // Limpiar dirección de entrega - puede venir con "undefined" concatenado
  const rawAddressLine = defaultData?.shipping_address?.address_line;
  const addressLine = cleanValue(rawAddressLine) || t('Sin dirección registrada');
  
  // Limpiar dimensiones del paquete
  const packageDimensions = defaultData?.packageDimensions 
    ? (() => {
        const h = cleanValue(defaultData.packageDimensions.height);
        const w = cleanValue(defaultData.packageDimensions.width);
        const l = cleanValue(defaultData.packageDimensions.length);
        const weight = cleanValue(defaultData.packageDimensions.weight);
        const dims = [h, w, l].filter(d => d && d !== '').join('x');
        const weightStr = weight ? `${weight}kg` : '';
        return dims && weightStr ? `${dims}, ${weightStr}` : dims || weightStr || t('Sin dimensiones registradas');
      })()
    : t('Sin dimensiones registradas');
  
  const orderId = cleanValue(defaultData?.order_id) || t('Sin número de venta registrado');
  
  const commentsRaw = defaultData?.comments ?? defaultData?.shipping_address?.comment ?? '';
  const comments = cleanValue(commentsRaw) || t('Sin comentarios');
  
  const isMercadoLibre = defaultData?.type === 'MercadoLibre';
  const packageNumber = cleanValue(defaultData?.followingNumber) || '';
  const packageType = cleanValue(defaultData?.type) || 'OTS';

  const typeLabelMap = {
    MERCADOLIBRE: 'MercadoLibre',
    WOOCOMMERCE: 'WooCommerce',
    SHOPIFY: 'Shopify',
    FALABELLA: 'Falabella',
    RIPLEY: 'Ripley',
    PRESTASHOP: 'PrestaShop',
    EXCEL: 'Excel',
    SENDU: 'Sendu',
    WIX: 'Wix',
    PARIS: 'Paris',
    OTS: 'Manual', // pedido creado a mano (type "OTS") -> "Manual"
  };
  const typeLabel = typeLabelMap[packageType.toUpperCase()] || packageType;

  const isDelivered = defaultData?.status === 4;
  // La etiqueta de devolución y el pedido de cambio se excluyen sobre un mismo padre
  // (no se retira dos veces el mismo producto). Acá solo se ocultan los casos que ya
  // se saben; el resto lo decide el backend con un 409.
  const exchangeCreatedHere = Boolean(exchangeChild && !exchangeChild.inProgress);
  const returnLabelExcluded =
    Boolean(defaultData?.exchanges?.returnedByChildId) || exchangeCreatedHere || returnBlockedByExchange;
  // El panel de una etiqueta que ya existe se sigue mostrando; el botón para crearla, no.
  const showReturnButton =
    isDelivered && !defaultData?.relatedTo && (Boolean(returnCreated && returnData) || !returnLabelExcluded);
  const showExchangeButton = !!defaultData?._id && canCreateExchange(user?.role, defaultData);

  const handleReturnLabelExists = ({ returnLabelId, followingNumber }) => {
    setExchangeOpen(false);
    setReturnCreated(true);
    if (followingNumber) setReturnData({ _id: returnLabelId, followingNumber });
  };

  const handleExchangeCreated = (child) => {
    setExchangeOpen(false);
    setExchangeChild({ followingNumber: child?.followingNumber ?? null, inProgress: false });
    onExchangeChanged?.({ _id: child?._id ?? null, followingNumber: child?.followingNumber ?? null });
  };

  const handleExchangeInProgress = ({ childId, followingNumber }) => {
    setExchangeOpen(false);
    setExchangeChild({ followingNumber, inProgress: true });
    onExchangeChanged?.({ _id: childId ?? null, followingNumber: followingNumber ?? null });
  };

  // Independiente de la etiqueta de devolución y también para MercadoLibre (el
  // hijo es un envío propio, sin vínculo con el marketplace): por eso se arma una
  // vez y se pinta en las dos variantes de la columna de acciones (botones de
  // descarga o QR de MercadoLibre).
  const exchangeBlock = showExchangeButton && (
    exchangeChild ? (
      <div className="pdm__return-info" style={{ marginTop: 0 }}>
        <div className="pdm__return-info__icon-wrap">
          <ArrowRightLeft aria-hidden="true" />
        </div>
        <div className="pdm__return-info__body">
          <span className="pdm__return-info__label">
            {exchangeChild.inProgress ? t("Cambio en curso") : t("Cambio creado")}
          </span>
          {exchangeChild.followingNumber && (
            <span className="pdm__return-info__number">#{exchangeChild.followingNumber}</span>
          )}
        </div>
        {exchangeChild.followingNumber && (
          <CopyButton
            value={exchangeChild.followingNumber}
            label={t("Copiar número de seguimiento")}
            copiedMessage={t("Número de seguimiento copiado")}
          />
        )}
      </div>
    ) : (
      // Con etiqueta de devolución (la que ya traía el pedido o la creada en esta
      // apertura) el producto ya se retira por ahí: no se ofrece el cambio.
      !returnCreated && (
        <button
          className="pdm__btn pdm__btn--secondary pdm__btn--full"
          onClick={() => setExchangeOpen(true)}
          type="button"
        >
          <ArrowRightLeft className="pdm__btn-icon" aria-hidden="true" />
          {t("Crear pedido de cambio")}
        </button>
      )
    )
  );

  return (
    <div className="pdm">
      {/* Header */}
      <div className="pdm__header">
        <div className="pdm__header-left">
          <span className="pdm__header-eyebrow">{t("Número de seguimiento")}</span>
          {/* El botón va fuera del <h3> para no ensuciar el nombre accesible del título. */}
          <div className="flex items-center gap-1">
            <h3 className="pdm__title">#{packageNumber}</h3>
            <CopyButton
              value={packageNumber}
              label={t("Copiar número de seguimiento")}
              copiedMessage={t("Número de seguimiento copiado")}
            />
          </div>
        </div>
        <span className={`pdm__type-badge pdm__type-badge--${packageType.toLowerCase()}`}>
          {t(typeLabel)}
        </span>
      </div>

      {/* Body - dos columnas en desktop */}
      <div className="pdm__body">
        {/* Columna izquierda: información */}
        <div className="pdm__col-info">
          {/* Destinatario */}
          <section className="pdm__section">
            <h4 className="pdm__section-title">
              <svg className="pdm__section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              {t("Destinatario")}
            </h4>
            <dl className="pdm__dl">
              <div className="pdm__dl-row pdm__dl-row--full">
                <dt>{t("Nombre")}</dt>
                {/* Nombre, teléfono y dirección se copian para responder por WhatsApp:
                    Clarity mostraba clics y selecciones a mano sobre estos datos. */}
                <dd>
                  {fullReceiverName}{" "}
                  <CopyButton
                    value={cleanConcatenated(receiverName, receiverLastName)}
                    label={t("Copiar nombre")}
                    copiedMessage={t("Nombre copiado")}
                  />
                </dd>
              </div>
              <div className="pdm__dl-row">
                <dt>{t("Teléfono")}</dt>
                <dd>
                  {receiverPhone}{" "}
                  {/* Solo un número real: el "XXXXXXX" de MercadoLibre no sirve copiado. */}
                  <CopyButton
                    value={isCallablePhone(defaultData?.receiver_phone) ? cleanValue(defaultData?.receiver_phone) : null}
                    label={t("Copiar teléfono")}
                    copiedMessage={t("Teléfono copiado")}
                  />
                </dd>
              </div>
              <div className="pdm__dl-row">
                <dt>{t("Comuna")}</dt>
                <dd>{municipality}</dd>
              </div>
              <div className="pdm__dl-row pdm__dl-row--full">
                <dt>{t("Dirección")}</dt>
                <dd>
                  {addressLine}{" "}
                  <CopyButton
                    value={[cleanValue(rawAddressLine), cleanValue(defaultData?.shipping_address?.municipality?.name)].filter(Boolean).join(", ")}
                    label={t("Copiar dirección")}
                    copiedMessage={t("Dirección copiada")}
                  />
                </dd>
              </div>
            </dl>
          </section>

          {/* Paquete */}
          <section className="pdm__section">
            <h4 className="pdm__section-title">
              <svg className="pdm__section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
              {t("Paquete")}
            </h4>
            <dl className="pdm__dl">
              <div className="pdm__dl-row">
                <dt>{t("Dimensiones")}</dt>
                <dd>{packageDimensions}</dd>
              </div>
              <div className="pdm__dl-row">
                <dt>{t("N.° de venta")}</dt>
                <dd>
                  {orderId}{" "}
                  {/* Valor crudo: orderId cae al placeholder cuando no hay número */}
                  <CopyButton
                    value={cleanValue(defaultData?.order_id)}
                    label={t("Copiar número de venta")}
                    copiedMessage={t("Número de venta copiado")}
                  />
                </dd>
              </div>
              <div className="pdm__dl-row pdm__dl-row--full">
                <dt>{t("Comentario")}</dt>
                <dd>{comments}</dd>
              </div>
            </dl>
          </section>
        </div>

        {/* Columna derecha: acciones o QR */}
        <div className="pdm__col-actions">
          {isMercadoLibre ? (
            /* QR MercadoLibre */
            <div className="pdm__qr-wrapper">
              <h4 className="pdm__section-title pdm__section-title--centered">
                <svg className="pdm__section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                  <rect x="3" y="14" width="7" height="7"/><path d="M14 14h3v3h-3zM17 17h3v3h-3zM14 20h3"/>
                </svg>
                {t("Etiqueta MercadoLibre")}
              </h4>
              {defaultData?.qr ? (
                <div className="pdm__qr-container">
                  <QRCode
                    value={defaultData.qr}
                    size={200}
                    style={{ height: "auto", maxWidth: "100%", width: "100%", display: "block" }}
                    viewBox="0 0 256 256"
                  />
                </div>
              ) : (
                <p className="pdm__qr-empty">{t("QR no disponible para este envío.")}</p>
              )}
            </div>
          ) : (
            /* Botones de descarga */
            <div className="pdm__actions">
              <h4 className="pdm__section-title">
                <svg className="pdm__section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                {t("Descargar etiqueta")}
              </h4>

              <div className="pdm__btn-grid">
                <button
                  className="pdm__btn pdm__btn--secondary"
                  onClick={downloadZpl}
                  type="button"
                >
                  <svg className="pdm__btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 6 2 18 2 18 9" />
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                    <rect x="6" y="14" width="12" height="8" rx="1" />
                  </svg>
                  <span className="pdm__btn-label">{t("Térmica")}</span>
                  <span className="pdm__btn-ext">.zpl</span>
                </button>

                <button
                  className="pdm__btn pdm__btn--secondary"
                  onClick={txt}
                  type="button"
                >
                  <svg className="pdm__btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                  <span className="pdm__btn-label">{t("Texto plano")}</span>
                  <span className="pdm__btn-ext">.txt</span>
                </button>

                <button
                  className="pdm__btn pdm__btn--secondary"
                  onClick={tryDownloadPdf}
                  type="button"
                >
                  <svg className="pdm__btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
                  </svg>
                  <span className="pdm__btn-label">PDF</span>
                  <span className="pdm__btn-ext">.pdf</span>
                </button>
              </div>

              {showReturnButton && (
                returnCreated && returnData ? (
                  <div className="pdm__return-info">
                    <div className="pdm__return-info__icon-wrap">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </div>
                    <div className="pdm__return-info__body">
                      <span className="pdm__return-info__label">{t("Devolución creada")}</span>
                      <span className="pdm__return-info__number">#{returnData.followingNumber}</span>
                    </div>
                    <button
                      className="pdm__return-info__copy"
                      onClick={handleCopyReturnNumber}
                      type="button"
                      title={t("Copiar número de seguimiento")}
                    >
                      {copied ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                        </svg>
                      )}
                      <span>{copied ? t("Copiado") : t("Copiar")}</span>
                    </button>
                  </div>
                ) : (
                  <button
                    className="pdm__btn pdm__btn--return pdm__btn--full"
                    onClick={handleCreateReturnLabel}
                    type="button"
                    disabled={isCreatingReturn || returnCreated}
                  >
                    <svg className="pdm__btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="1 4 1 10 7 10"/>
                      <path d="M3.51 15a9 9 0 1 0 .49-3.52"/>
                    </svg>
                    {isCreatingReturn ? t("Creando...") : t("Crear etiqueta para devolución")}
                  </button>
                )
              )}

              {exchangeBlock}

              {defaultData?._id && (
                <div className="pdm__qr-wrapper" style={{ marginTop: '1.5rem', transform: 'scale(0.8)', transformOrigin: 'top center', marginBottom: '-10%' }}>
                  <h4 className="pdm__section-title pdm__section-title--centered">
                    <svg className="pdm__section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                      <rect x="3" y="14" width="7" height="7"/><path d="M14 14h3v3h-3zM17 17h3v3h-3zM14 20h3"/>
                    </svg>
                    {t("Código QR")}
                  </h4>
                  <div className="pdm__qr-container">
                    <QRCode
                      value={JSON.stringify({ id: defaultData._id })}
                      size={170}
                      style={{ height: "auto", maxWidth: "100%", width: "100%", display: "block" }}
                      viewBox="0 0 256 256"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          {isMercadoLibre && exchangeBlock}
        </div>
      </div>

      {exchangeOpen && (
        <CreateExchangeDialog
          parentPackage={defaultData}
          role={user?.role}
          address={{ receiver: fullReceiverName, line: addressLine, municipality }}
          onClose={() => setExchangeOpen(false)}
          onCreated={handleExchangeCreated}
          onInProgress={handleExchangeInProgress}
          onReturnLabelExists={handleReturnLabelExists}
        />
      )}
    </div>
  );
}
